import React, { useState } from "react";
import { IconButton, Stack, Typography, useTheme } from "@mui/material";
import Button from "../../Button";
import CloseIcon from "@mui/icons-material/Close";
import { CARD_STYLES } from "../../../constants/styles";

interface GenericBannerProps {
    title: string;
    description?: string;
    primaryCTA: { label: string; onClick: () => void };
    secondaryCTA?: { label: string; onClick: () => void };
    closable?: boolean;
    onClose?: () => void;
    variant?: "primary" | "white";
}

const GenericBanner: React.FC<GenericBannerProps> = ({
    title,
    description,
    primaryCTA,
    secondaryCTA,
    closable = true,
    onClose,
    variant = "primary",
}) => {
    const theme = useTheme();
    const [visible, setVisible] = useState(true);

    const handleClose = () => {
        setVisible(false);
        if (onClose) onClose();
    };

    if (!visible) return null;

    const isPrimary = variant === "primary";

    return (
        <Stack
            direction="column"
            padding={2}
            width="100%"
            bgcolor={isPrimary ? theme.palette.primary.main : "white"}
            borderRadius={CARD_STYLES.borderRadius}
            boxShadow={CARD_STYLES.boxShadow}
            border={
                isPrimary ? "none" : `1px solid ${theme.palette.primary.main}`
            }
            sx={{ boxSizing: "border-box" }}
            position="relative"
            spacing={1.5}
        >
            {/* Close Button */}
            {closable && (
                <IconButton
                    onClick={handleClose}
                    size="small"
                    sx={{
                        position: "absolute",
                        top: 4,
                        right: 4,
                        color: isPrimary ? "white" : theme.palette.primary.main,
                    }}
                >
                    <CloseIcon fontSize="medium" />
                </IconButton>
            )}

            {/* Text Content */}
            <Stack direction="column" spacing={0.5} sx={{ pr: closable ? 4 : 0 }}>
                <Typography
                    fontWeight={600}
                    fontSize="16px"
                    color={isPrimary ? "white" : "black"}
                >
                    {title}
                </Typography>
                {description && (
                    <Typography
                        fontWeight={400}
                        fontSize="14px"
                        color={
                            isPrimary
                                ? "rgba(255, 255, 255, 0.8)"
                                : "textSecondary"
                        }
                    >
                        {description}
                    </Typography>
                )}
            </Stack>

            {/* Buttons */}
            <Stack direction="column" spacing={1}>
                {secondaryCTA && (
                    <Button
                        variant="small"
                        fullWidth
                        color={isPrimary ? "secondary" : "primary"}
                        onClick={secondaryCTA.onClick}
                    >
                        {secondaryCTA.label}
                    </Button>
                )}
                <Button
                    variant="small"
                    fullWidth
                    color={isPrimary ? "secondary" : "primary"}
                    onClick={primaryCTA.onClick}
                >
                    {primaryCTA.label}
                </Button>
            </Stack>
        </Stack>
    );
};

export default GenericBanner;
